import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useFavorites } from '../../hooks/useFavorites';
import { useCart } from '../../hooks/useCart';
import { getListingById, getAllListings, getSellerListings, type Listing, incrementViews } from '../../services/listings';
import { getTicketLinkByListingId } from '../../services/ticketLinks';
import { auth } from '../../lib/firebase';
import { getWallet, withdraw, fundWallet } from '../../services/wallet';
import { getUserProfile } from '../../services/auth';
import { getAverageResponseTime, recordListingView, recordUniqueListingViewer } from '../../services/stats';
import { getListingReviews, addReview, getAverageRatingForListing, hasUserPurchasedProduct } from '../../services/reviews';
import { createConversation } from '../../services/messaging';
import { getCashbackSettings, calculateCashbackForAmount } from '../../services/settings';
import { creditWallet } from '../../services/wallet';
import { createOrdersSplitBySeller } from '../../services/orders';
import { orderReceivedEmail } from '../../services/email';
import { toUserMessage } from '../../utils/errors';
import { useLiveWallet } from '../../hooks/useLiveWallet';
import { formatDate } from '../../utils/date';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  images: string[];
  description: string;
  category: string;
  condition: string;
  seller: {
    name: string;
    rating: number;
    totalSales: number;
    responseTime: string;
    university: string;
  };
  specifications: { [key: string]: string };
  tags: string[];
  availability: 'available' | 'sold' | 'reserved';
  postedDate: string;
  views: number;
  sellerId?: string;
  location?: string;
  distance?: string;
  type?: 'item' | 'house' | 'ticket';
  ticketTiers?: Array<{ name: string; price: number; quantity: number; perks?: string }>;
  deliveryOption?: boolean;
  pickupOption?: boolean;
}

const ProductPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { favoriteItems, addToFavorites, removeFromFavorites, isFavorite: isFavoriteFn } = useFavorites();
  const { addToCart } = useCart();
  const { wallet } = useLiveWallet();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showInsufficientFunds, setShowInsufficientFunds] = useState(false);
  const [sellerProfile, setSellerProfile] = useState<{ fullName: string | null; email: string | null; university?: string | null } | null>(null);
  const [sellerListings, setSellerListings] = useState<Listing[]>([]);
  const [similarListings, setSimilarListings] = useState<Listing[]>([]);
  const [activeTab, setActiveTab] = useState<'seller' | 'suggested'>('seller');
  const [suggestedListings, setSuggestedListings] = useState<Listing[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [ratingStats, setRatingStats] = useState<{ average: number; count: number }>({ average: 0, count: 0 });
  const [newRating, setNewRating] = useState<number>(5);
  const [newComment, setNewComment] = useState<string>('');
  const [reviewError, setReviewError] = useState<string>('');
  const [hasPurchased, setHasPurchased] = useState<boolean>(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState<boolean>(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  // Cashback preview and simple purchase form state
  const [cashbackInfo, setCashbackInfo] = useState<{ amount: number; enabled: boolean } | null>(null);
  const [purchaseForm, setPurchaseForm] = useState<{
    name: string;
    phone: string;
    address: string;
    shareLocation: boolean;
    buyerNote: string;
    coordinates: undefined | { lat: number; lng: number };
    fulfillmentMethod: 'delivery' | 'pickup';
  }>({ name: '', phone: '', address: '', shareLocation: false, buyerNote: '', coordinates: undefined, fulfillmentMethod: 'delivery' });
  
  // Ticket tier selection state
  const [selectedTierIndex, setSelectedTierIndex] = useState<number | null>(null);

  // Purchase flow state
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(null);
  const [locationHint, setLocationHint] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; phone?: string; address?: string; tier?: string; note?: string }>({});

  // Compute effective price based on selected tier
  const effectivePrice = (() => {
    if (!product) return 0;
    if (selectedTierIndex !== null && product.ticketTiers && product.ticketTiers[selectedTierIndex]) {
      return product.ticketTiers[selectedTierIndex].price;
    }
    return product.price || 0;
  })();
// Preview potential cashback for the current product price
  useEffect(() => {
    async function previewCashback() {
      try {
        const settings = await getCashbackSettings();
        const price = effectivePrice || 0;
        const cb = calculateCashbackForAmount(price, settings);
        setCashbackInfo({ amount: cb, enabled: settings.enabled });
      } catch (e) {
        // ignore preview errors
      }
    }
    previewCashback();
  }, [effectivePrice]);

  // Default fulfillment method when the product loads: pickup-only listings start on pickup,
  // everything else defaults to delivery so address + live-location fields are visible.
  useEffect(() => {
    if (!product) return;
    const pickupOnly = !!product.pickupOption && !product.deliveryOption;
    setPurchaseForm(prev => ({ ...prev, fulfillmentMethod: pickupOnly ? 'pickup' : 'delivery' }));
  }, [product?.id, product?.deliveryOption, product?.pickupOption]);

  // Validate the purchase form. Returns true if valid; also sets inline field errors and a
  // summary error. Ensures name/phone/address-or-location/tier/note are all sane before we
  // take money.
  const validatePurchaseForm = (): boolean => {
    const errors: { name?: string; phone?: string; address?: string; tier?: string; note?: string } = {};

    const name = purchaseForm.name.trim();
    if (!name) errors.name = 'Please enter your name.';
    else if (name.length < 2) errors.name = 'Name is too short.';
    else if (name.length > 80) errors.name = 'Name is too long.';
    else if (!/^[A-Za-zÀ-ÿ' .\-]+$/.test(name)) errors.name = 'Name contains invalid characters.';

    const phoneDigits = purchaseForm.phone.replace(/[^\d]/g, '');
    if (!purchaseForm.phone.trim()) errors.phone = 'Please enter your phone number.';
    else if (phoneDigits.length < 10 || phoneDigits.length > 15) errors.phone = 'Enter a valid phone number.';
    else if (/^0/.test(phoneDigits) && phoneDigits.length !== 11) errors.phone = 'Nigerian numbers starting with 0 must be 11 digits.';
    else if (/^234/.test(phoneDigits) && phoneDigits.length !== 13) errors.phone = 'Numbers starting with 234 must be 13 digits.';

    if (purchaseForm.fulfillmentMethod === 'delivery') {
      const hasAddress = purchaseForm.address.trim().length >= 5;
      const hasLocation = !!(purchaseForm.shareLocation && purchaseForm.coordinates);
      if (!hasAddress && !hasLocation) {
        errors.address = 'Enter a delivery address (min 5 chars) or turn on your live location.';
      } else if (purchaseForm.address.trim().length > 250) {
        errors.address = 'Address is too long.';
      }
    }

    if (product?.type === 'ticket' && product?.ticketTiers && product.ticketTiers.length > 0 && selectedTierIndex === null) {
      errors.tier = 'Please select a ticket tier.';
    }

    if (purchaseForm.buyerNote && purchaseForm.buyerNote.length > 500) {
      errors.note = 'Buyer note is too long (max 500 characters).';
    }

    setFieldErrors(errors);
    const firstError = Object.values(errors)[0];
    if (firstError) {
      setPurchaseError(firstError);
      return false;
    }
    setPurchaseError(null);
    return true;
  };

  // Initialize Paystack payment for buying a single product
  const initializePaystackPurchase = () => {
    setPurchaseError(null);
    if (!product) return;
    if (!validatePurchaseForm()) return;
    const user = auth.currentUser;
    if (!user) { setPurchaseError('You need to sign in to purchase.'); return; }
    if (!user.email) { setPurchaseError('A verified email is required for Paystack payment.'); return; }
    const paystackKey = (import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string) || '';
    if (!paystackKey) { setPurchaseError('Paystack is not configured. Please contact support.'); return; }
    if (!(window as any).PaystackPop || typeof (window as any).PaystackPop.setup !== 'function') {
      setPurchaseError('Paystack is not available. Please check your connection and reload.');
      return;
    }
    if (!effectivePrice || effectivePrice <= 0) { setPurchaseError('Invalid price.'); return; }

    setIsPurchasing(true);
    try {
      const ref = `CM-PROD-PS-${Date.now()}-${user.uid}`;
      const handler = (window as any).PaystackPop.setup({
        key: paystackKey,
        email: user.email,
        amount: Math.round(effectivePrice * 100),
        currency: 'NGN',
        ref,
        callback: (response: any) => {
          (async () => {
            try {
              const reference = response?.reference || ref;
              // Credit wallet with Paystack proceeds, then purchase via wallet/escrow flow
              await fundWallet(user.uid, effectivePrice, 'paystack', reference);
              await handlePurchaseSuccess(reference);
            } catch (e) {
              console.error('Paystack purchase failed after payment', e);
              setPurchaseError('Payment went through but order creation failed. Contact support with reference: ' + (response?.reference || ref));
            } finally {
              setIsPurchasing(false);
            }
          })();
        },
        onClose: function () {
          setIsPurchasing(false);
        }
      });
      if (handler && typeof handler.openIframe === 'function') {
        handler.openIframe();
      } else {
        setIsPurchasing(false);
        setPurchaseError('Unable to open Paystack payment window.');
      }
    } catch (e) {
      console.error('Paystack init failed', e);
      setIsPurchasing(false);
      setPurchaseError('Unable to start payment. Please try again.');
    }
  };

  const handleWalletPurchase = async () => {
    setPurchaseError(null);
    if (!product) return;
    if (!validatePurchaseForm()) return;
    const user = auth.currentUser;
    if (!user) { setPurchaseError('You need to sign in to purchase.'); return; }
    if (!effectivePrice || effectivePrice <= 0) { setPurchaseError('Invalid price.'); return; }

    setIsPurchasing(true);
    try {
      let balance = typeof wallet?.balance === 'number' ? wallet!.balance : undefined;
      if (typeof balance !== 'number') {
        const w = await getWallet(user.uid);
        balance = w?.balance ?? 0;
      }
      if ((balance || 0) < effectivePrice) {
        setIsPurchasing(false);
        setShowPurchaseModal(false);
        setShowInsufficientFunds(true);
        return;
      }
      const reference = `CM-PROD-WLT-${Date.now()}-${user.uid}`;
      await handlePurchaseSuccess(reference);
    } catch (e) {
      console.error('Wallet purchase failed', e);
      setPurchaseError('Unable to complete wallet payment. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  // After successful payment, create order and apply cashback
  const handlePurchaseSuccess = async (reference: string) => {
    try {
      const user = auth.currentUser;
      if (!user || !product) return;
      // Debit buyer wallet balance before holding escrow
      await withdraw(user.uid, effectivePrice, { description: `Purchase - escrow hold (₦${effectivePrice.toLocaleString()})`, reference });
      const cartItems = [{
  id: product.id,
  listingId: product.id,
  title: product.name,
  price: effectivePrice,
  image: product.images?.[0],
  quantity: 1,
  sellerId: product.sellerId || 'unknown',
  sellerName: product.seller?.name || 'Seller',
  metadata: (selectedTierIndex !== null && product.ticketTiers && product.ticketTiers[selectedTierIndex])
    ? { tier: product.ticketTiers[selectedTierIndex].name, tierPrice: product.ticketTiers[selectedTierIndex].price }
    : undefined,
}];

      const { ordersBySeller } = await createOrdersSplitBySeller({
        buyerId: user.uid,
        cartItems,
        paymentReference: reference,
        buyerDetails: {
          buyerName: purchaseForm.name || user.displayName || undefined,
          buyerPhone: purchaseForm.phone || undefined,
          buyerEmail: user.email || undefined,
          deliveryAddress: purchaseForm.address || undefined,
          shareLocation: purchaseForm.shareLocation || false,
          buyerNote: purchaseForm.buyerNote || undefined,
          coordinates: purchaseForm.fulfillmentMethod === 'delivery' && purchaseForm.shareLocation ? purchaseForm.coordinates : undefined,
        },
      });

      // Notify each seller of the received order
      try {
        const buyerProfile = await getUserProfile(user.uid);
        const buyerName = buyerProfile.fullName || undefined;
        for (const pair of ordersBySeller) {
          const sellerProfile = await getUserProfile(pair.sellerId);
          if (sellerProfile.email) {
            await orderReceivedEmail(sellerProfile.email, pair.orderId, buyerName);
          }
        }
      } catch (e) {
        console.warn('Failed to send order-received emails', e);
      }

      // Apply cashback if enabled
      try {
        const settings = await getCashbackSettings();
        const cb = calculateCashbackForAmount(effectivePrice, settings);
        if (settings.enabled && cb > 0) {
          await creditWallet(user.uid, cb, `Cashback on purchase of ₦${effectivePrice.toLocaleString()}`);
        }
      } catch (e) {
        console.warn('Cashback credit failed', e);
      }

      setShowPurchaseModal(false);
      alert('Purchase successful! Payment held in escrow. Seller has been notified.');
      navigate('/orders');
    } catch (e) {
      console.error('Failed to complete purchase', e);
      alert('Failed to complete purchase. Please try again.');
    }
  };

  // Mock presence since usePresence hook is missing
  const sellerPresence = { online: true };

  const isVideo = (url: string) => /(\.mp4|\.webm|\.ogg)$/i.test(url);

  function getShareMessageByCategory(cat?: string, title?: string, price?: number) {
    const short = (title || '').slice(0, 80);
    const p = typeof price === 'number' ? `₦${price.toLocaleString()}` : '';
    const key = (cat || '').toLowerCase();
    switch (key) {
      case 'tech':
        return `Tech deal on Campus Market: ${short}${p ? ` — ${p}` : ''}`;
      case 'electronics':
        return `Electronics find on Campus Market: ${short}${p ? ` — ${p}` : ''}`;
      case 'fashion':
        return `Fashion steal on Campus Market: ${short}${p ? ` — ${p}` : ''}`;
      case 'books':
        return `Book bargain on Campus Market: ${short}${p ? ` — ${p}` : ''}`;
      case 'appliances':
        return `Home/appliance deal on Campus Market: ${short}${p ? ` — ${p}` : ''}`;
      case 'furniture':
        return `Furniture find on Campus Market: ${short}${p ? ` — ${p}` : ''}`;
      case 'services':
        return `Campus services offering: ${short}${p ? ` — ${p}` : ''}`;
      default:
        return `Hot deal on Campus Market: ${short}${p ? ` — ${p}` : ''}`;
    }
  }

  function buildShareUrl() {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const params = new URLSearchParams({
      id: product?.id || ''
    });
    return `${origin}/api/product-share?${params.toString()}`;
  }

  useEffect(() => {
    // initialize data load
    (async () => {
      if (!id) return;
      const listing = await getListingById(id);
      if (!listing) return;
      // Increment views once per cache TTL (per product)
      try {
        const cacheKey = 'cm_listing_views';
        const ttlMs = 12 * 60 * 60 * 1000; // 12 hours
        const raw = typeof window !== 'undefined' ? localStorage.getItem(cacheKey) : null;
        const map = raw ? JSON.parse(raw) : {};
        const last = map[listing.id!];
        const now = Date.now();
        if (!last || (now - last) > ttlMs) {
          await incrementViews(listing.id!);
          if (listing.sellerId) {
            try { await recordListingView(listing.id!, listing.sellerId); } catch {}
            const viewerId = auth.currentUser?.uid;
            if (viewerId && viewerId !== listing.sellerId) {
              try { await recordUniqueListingViewer(listing.id!, listing.sellerId, viewerId); } catch {}
            }
          }
          map[listing.id!] = now;
          if (typeof window !== 'undefined') localStorage.setItem(cacheKey, JSON.stringify(map));
        }
      } catch {}
      const mapped: Product = {
        id: listing.id!,
        name: listing.title,
        price: listing.price,
        originalPrice: listing.originalPrice ?? undefined,
        images: listing.images ?? (listing.image ? [listing.image] : []),
        description: listing.description ?? '',
        category: listing.category ?? '',
        condition: listing.condition ?? '',
        seller: {
          name: listing.seller ?? '',
          rating: typeof listing.rating !== 'undefined' ? listing.rating : 0,
          totalSales: 0,
          responseTime: '',
          university: ''
        },
        specifications: listing.specifications ?? {},
        tags: [],
        availability: 'available',
        postedDate: listing.timePosted ?? '',
        views: 0,
        sellerId: listing.sellerId,
        location: listing.location,
        distance: listing.distance,
        type: listing.type,
        ticketTiers: listing.ticketTiers || []
      };
      // Tickets are sold on the dedicated guest-checkout page — send buyers there
      // instead of the generic product page (which uses the wallet/login flow).
      // Detect by type, category, or presence of ticket tiers (older tickets vary).
      const isTicketListing = listing.type === 'ticket'
        || (listing.category || '').toLowerCase() === 'tickets'
        || (Array.isArray(listing.ticketTiers) && listing.ticketTiers.length > 0);
      if (isTicketListing && listing.id) {
        setRedirecting(true);
        const link = await getTicketLinkByListingId(listing.id, { sellerId: listing.sellerId, title: listing.title });
        if (link?.id) { navigate(`/tickets/${link.id}`, { replace: true }); return; }
        setRedirecting(false); // no link found (older ticket) — show the product page as fallback
      }

      setProduct(mapped);

      if (listing.type === 'ticket' && Array.isArray(listing.ticketTiers) && listing.ticketTiers.length > 0) {
        setSelectedTierIndex(0);
      }

      // Fetch seller profile and listings
      if (listing.sellerId) {
        const profile = await getUserProfile(listing.sellerId);
        setSellerProfile(profile);
        const sellerLs = await getSellerListings(listing.sellerId);
        setSellerListings(sellerLs.filter(l => l.id !== listing.id));
        setProduct(prev => prev ? { ...prev, seller: { ...prev.seller, university: profile.university || '', totalSales: sellerLs.length } } : prev);
      }

      // Fetch similar listings by category
      if (listing.category) {
        const all = await getAllListings();
        const similar = all.filter(l => l.category === listing.category && l.id !== listing.id).slice(0, 8);
        setSimilarListings(similar);
      }

      // Fetch random suggestions (Products you might like)
      const allForSuggestions = await getAllListings();
      const randomSuggestions = allForSuggestions
        .filter(l => l.id !== listing.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 8);
      setSuggestedListings(randomSuggestions);

      // Reviews and rating stats
      const r = await getListingReviews(listing.id!);
      setReviews(r);
      const stats = await getAverageRatingForListing(listing.id!);
      setRatingStats(stats);

      // Average response time
      const rt = await getAverageResponseTime();
      setProduct(prev => prev ? { ...prev, seller: { ...prev.seller, responseTime: rt } } : prev);
      
      // Check if current user has purchased this product
      if (auth.currentUser && listing.id) {
        try {
          const purchased = await hasUserPurchasedProduct(auth.currentUser.uid, listing.id);
          setHasPurchased(purchased);
        } catch (error) {
          console.error('Error checking purchase status:', error);
          setHasPurchased(false);
        }
      }
    })();
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prompt = params.get('promptReview') === '1';
    if (prompt && hasPurchased && product && auth.currentUser) {
      const already = reviews.some(r => r.reviewerId === auth.currentUser!.uid);
      if (!already) setShowReviewPrompt(true);
    }
  }, [location.search, hasPurchased, product, reviews]);

  const isFavorite = product ? isFavoriteFn(product.id) : false;

  const handleFavoriteToggle = () => {
    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }

    if (!product) return;

    if (isFavorite) {
      removeFromFavorites(product.id);
    } else {
      addToFavorites({
        id: product.id,
        name: product.name,
        price: `₦${effectivePrice.toLocaleString()}`,
        image: product.images[0]
      });
    }
  };

  const handleAddToCart = () => {
  if (!auth.currentUser) {
    setShowLoginPrompt(true);
    return;
  }

  if (!product) return;

  const tierMeta = selectedTierIndex !== null && product.ticketTiers && product.ticketTiers[selectedTierIndex]
    ? { tier: product.ticketTiers[selectedTierIndex].name, tierPrice: product.ticketTiers[selectedTierIndex].price }
    : undefined;

  addToCart({
    id: product.id,
    name: product.name,
    price: effectivePrice,
    image: product.images[0]
  }, 1, tierMeta);

  alert('Added to cart successfully!');
};

  const handleBuyNow = async () => {
    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    setShowPurchaseModal(true);
  };

  const handleContactSeller = async () => {
    if (!auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    if (!product || !product.sellerId) return;
    const conversationId = await createConversation(
      product.sellerId,
      product.seller.name || 'Seller',
      sellerProfile?.email || '',
      product.id,
      product.name
    );
    navigate(`/messages?conversation=${conversationId}`);
  };

  if (redirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Opening ticket…</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <i className="ri-arrow-left-line text-xl mr-2"></i>
              Back
            </button>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleFavoriteToggle}
                      className={`p-2 ${isFavorite ? 'text-red-600' : 'text-gray-400'} hover:text-red-600`}
              >
                <i className={`text-xl ${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600" onClick={() => setShowShareModal(true)}>
                <i className="ri-share-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Images */}
          <div className="space-y-4">
            <div
              className="relative aspect-square bg-white rounded-lg overflow-hidden shadow-sm group"
              onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touchStartX.current == null || product.images.length < 2) return;
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                const n = product.images.length;
                if (dx < -40) setSelectedImageIndex((i) => (i + 1) % n);
                else if (dx > 40) setSelectedImageIndex((i) => (i - 1 + n) % n);
                touchStartX.current = null;
              }}
            >
              {isVideo(product.images[selectedImageIndex]) ? (
                <video src={product.images[selectedImageIndex]} controls className="w-full h-full object-cover" />
              ) : (
                <img
                  src={product.images[selectedImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              )}
              {product.images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedImageIndex((i) => (i - 1 + product.images.length) % product.images.length)}
                    aria-label="Previous image"
                    className="absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-gray-800 shadow-md backdrop-blur-sm transition-opacity hover:bg-white md:opacity-0 md:group-hover:opacity-100"
                  >
                    <i className="ri-arrow-left-s-line text-xl"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedImageIndex((i) => (i + 1) % product.images.length)}
                    aria-label="Next image"
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-gray-800 shadow-md backdrop-blur-sm transition-opacity hover:bg-white md:opacity-0 md:group-hover:opacity-100"
                  >
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </button>
                  <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">{selectedImageIndex + 1}/{product.images.length}</span>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {product.images.map((_, i) => (
                      <span key={i} className={`h-1.5 rounded-full transition-all ${i === selectedImageIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/60'}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                      className={`text-left border rounded-lg p-3 transition-colors ${selectedImageIndex === index ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                >
                  {isVideo(image) ? (
                    <div className="relative w-full h-full">
                      <video src={image} className="w-full h-full object-cover" muted />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <i className="ri-play-circle-line text-white text-2xl drop-shadow"></i>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                  {product.availability}
                </span>
                <span className="text-sm text-gray-500">{product.views} views</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-blue-600">₦{effectivePrice.toLocaleString()}</span>
                {product.originalPrice && (
                  <span className="text-xl text-gray-500 line-through">₦{product.originalPrice.toLocaleString()}</span>
                )}
              </div>
              {cashbackInfo?.enabled && cashbackInfo.amount > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <p className="text-sm text-teal-800">
                    Cashback preview for this order: ₦{cashbackInfo.amount.toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Ticket Tier Selection */}
            {product.ticketTiers && product.ticketTiers.length > 0 && (
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">Choose Ticket Tier</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {product.ticketTiers.map((tier, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedTierIndex(idx)}
                      className={`text-left border rounded-lg p-3 transition-colors ${selectedTierIndex === idx ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900">{tier.name}</p>
                        <span className="text-blue-600 font-semibold">{tier.price.toLocaleString()}</span>
                      </div>
                      {tier.perks && <p className="text-sm text-gray-600 mt-1">{tier.perks}</p>}
                      {typeof tier.quantity === 'number' && tier.quantity > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{tier.quantity} available</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Seller Info */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">About the Seller</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Seller Avatar */}
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 border">
                    {sellerProfile?.photoURL ? (
                      <img src={sellerProfile.photoURL} alt={product.seller.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <i className="ri-user-line text-2xl"></i>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      {product.seller.name}
                      {sellerProfile?.isVerified && ( // Use isVerified for verification badge
                        <span className="inline-flex items-center px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">
                          <i className="ri-shield-check-line mr-1"></i>
                          Verified
                        </span>
                      )}
                      {product.seller.rating > 0 && ( // Display rating if greater than 0
                        <span className="inline-flex items-center text-xs text-yellow-600">
                          <i className="ri-star-fill mr-1"></i>
                          {product.seller.rating.toFixed(1)}
                        </span>
                      )}
                      <span className={`inline-flex items-center text-xs ${sellerPresence?.online ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className={`w-2 h-2 rounded-full ${sellerPresence?.online ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        <span className="ml-1">{sellerPresence?.online ? 'Online' : 'Offline'}</span>
                      </span>
                    </p>
                    <p className="text-sm text-gray-600">{product.seller.university}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-2">
                      <div className="flex items-center">
                        <i className="ri-star-fill text-yellow-400 text-sm mr-1"></i>
                        <span className="text-sm text-gray-600">{ratingStats.average.toFixed(1)} ({ratingStats.count})</span>
                      </div>
                      <span className="text-sm text-gray-600">{product.seller.totalSales} listings</span>
                      {product.location && product.location.trim().toLowerCase() !== (product.seller.university || '').trim().toLowerCase() && (
                        <span className="text-sm text-gray-600">Location: {product.location}</span>
                      )}
                      {product.distance && (
                        <span className="text-sm text-gray-600">Distance: {product.distance}</span>
                      )}
                      <span className="text-sm text-gray-600">Responds in {product.seller.responseTime}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleContactSeller}
                    className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    <i className="ri-chat-3-line"></i>
                    Message seller about this
                  </button>
                  {product.sellerId && (
                    <button
                      onClick={() => navigate(`/seller/${product.sellerId}`)}
                      className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      View Seller Profile
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleBuyNow}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Buy Now - ₦{effectivePrice.toLocaleString()}
              </button>
              <button
                onClick={handleAddToCart}
                className="w-full bg-gray-100 text-gray-900 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Add to Cart
              </button>
            </div>

            {/* Product Details */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Product Details</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="text-gray-900">{product.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Condition:</span>
                  <span className="text-gray-900">{product.condition}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Posted:</span>
                  <span className="text-gray-900">{formatDate(product.postedDate) || 'N/A'}</span>
                </div>
                {product.deliveryOption !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery:</span>
                    <span className="text-gray-900">{product.deliveryOption ? 'Yes' : 'No'}</span>
                  </div>
                )}
                {product.pickupOption !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pickup:</span>
                    <span className="text-gray-900">{product.pickupOption ? 'Yes' : 'No'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Specifications</h3>
              <div className="space-y-2">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600">{key}:</span>
                    <span className="text-gray-900">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-8 bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
          <p className="text-gray-700 leading-relaxed">{product.description}</p>
          
          {product.tags.length > 0 && (
            <div className="mt-4">
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Reviews */}
        <div className="mt-6 bg-white rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Reviews</h2>
            <div className="flex items-center">
              {[1,2,3,4,5].map(star => (
                <i key={star} className={`ri-star-fill ${star <= Math.round(ratingStats.average) ? 'text-yellow-400' : 'text-gray-300'} mr-1`}></i>
              ))}
              <span className="ml-2 text-sm text-gray-600">{ratingStats.average.toFixed(1)} / 5 • {ratingStats.count} reviews</span>
            </div>
          </div>
          {reviews.length === 0 ? (
            <p className="text-gray-600">No reviews yet. Be the first to review this item.</p>
          ) : (
            <div className="space-y-4">
              {reviews.slice(0,5).map((rev) => (
                <div key={rev.id} className="border border-gray-100 rounded-md p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">{rev.reviewerName}</p>
                    <div className="flex items-center">
                      {[1,2,3,4,5].map(star => (
                        <i key={star} className={`ri-star-fill ${star <= rev.rating ? 'text-yellow-400' : 'text-gray-300'} mr-1`}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700 mt-2">{rev.comment}</p>
                  <p className="text-xs text-gray-500 mt-1">{rev.createdAt?.toDate ? rev.createdAt.toDate().toLocaleString() : ''}</p>
                </div>
              ))}
            </div>
          )}

          {/* Add Review */}
          <div className="mt-6">
            <h3 className="font-semibold text-gray-900 mb-2">Add your review</h3>
            {!auth.currentUser ? (
              <p className="text-sm text-gray-600">Please log in to leave a review.</p>
            ) : !hasPurchased ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <i className="ri-shopping-cart-line text-yellow-600 text-xl mr-3 mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Purchase Required</p>
                    <p className="text-sm text-yellow-700 mt-1">You can only review products you have purchased. Buy this item to share your experience!</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Rating:</span>
                  {[1,2,3,4,5].map(star => (
                    <button key={star} onClick={() => setNewRating(star)}>
                      <i className={`ri-star-fill ${star <= newRating ? 'text-yellow-400' : 'text-gray-300'} text-xl`}></i>
                    </button>
                  ))}
                </div>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write your review..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {reviewError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{reviewError}</p>
                  </div>
                )}
                <button
                  onClick={async () => {
                    if (!product) return;
                    setReviewError('');
                    try {
                      await addReview(product.id, newRating, newComment);
                      setNewComment('');
                      const r = await getListingReviews(product.id);
                      setReviews(r);
                      const stats = await getAverageRatingForListing(product.id);
                      setRatingStats(stats);
                    } catch (error: any) {
                      setReviewError(toUserMessage(error));
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Submit Review
                </button>
              </div>
            )}
          </div>
        </div>

        {/* More from this seller & Similar items */}
        {(sellerListings.length > 0 || suggestedListings.length > 0) && (
          <div className="mt-6 bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-200 mb-4">
              <button
                onClick={() => setActiveTab('seller')}
                className={`px-3 py-2 text-sm font-medium ${activeTab === 'seller' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                Other listings from seller
              </button>
              <button
                onClick={() => setActiveTab('suggested')}
                className={`px-3 py-2 text-sm font-medium ${activeTab === 'suggested' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                Products you might like
              </button>
            </div>
        
            {activeTab === 'seller' && sellerListings.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sellerListings.slice(0,6).map(item => (
                  <Link key={item.id} to={`/product/${item.id}`} className="border border-gray-100 rounded-lg overflow-hidden hover:shadow-md transition">
                    {item.image || (item.images && item.images[0]) ? (
                      <img src={(item.images && item.images[0]) || (item.image as string)} alt={item.title} className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 bg-gray-100" />
                    )}
                    <div className="p-3">
                      <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                      <p className="text-teal-600 font-semibold">₦{Number(item.price || 0).toLocaleString()}</p>
                    </div>
                  </Link>
                ))}
                {sellerListings.length === 0 && (
                  <p className="text-sm text-gray-600">No other listings from this seller.</p>
                )}
              </div>
            )}
        
            {activeTab === 'suggested' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {suggestedListings.slice(0,6).map(item => (
                  <Link key={item.id} to={`/product/${item.id}`} className="border border-gray-100 rounded-lg overflow-hidden hover:shadow-md transition">
                    {item.image || (item.images && item.images[0]) ? (
                      <img src={(item.images && item.images[0]) || (item.image as string)} alt={item.title} className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 bg-gray-100" />
                    )}
                    <div className="p-3">
                      <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                      <p className="text-teal-600 font-semibold">₦{Number(item.price || 0).toLocaleString()}</p>
                    </div>
                  </Link>
                ))}
                {suggestedListings.length === 0 && (
                  <p className="text-sm text-gray-600">No suggestions available right now.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Similar items */}
        {similarListings.length > 0 && (
          <div className="mt-6 bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Similar items you may like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {similarListings.slice(0,6).map(item => (
                <Link key={item.id} to={`/product/${item.id}`} className="border border-gray-100 rounded-lg overflow-hidden hover:shadow-md transition">
                  {item.image || (item.images && item.images[0]) ? (
                    <img src={(item.images && item.images[0]) || item.image!} alt={item.title} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-gray-100" />
                  )}
                  <div className="p-3">
                    <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                    <p className="text-teal-600 font-semibold">₦{Number(item.price || 0).toLocaleString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Support and Reporting */}
        <div className="mt-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to={`/support`} className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <i className="ri-lifebuoy-line mr-1"></i>
              Contact Support
            </Link>
            {product && (
              <Link to={`/report?type=product&id=${product.id}&title=${encodeURIComponent(product.name)}&seller=${encodeURIComponent(product.seller.name)}`} className="inline-flex items-center justify-center rounded-md bg-red-600 text-white px-3 py-2 text-sm hover:bg-red-700">
                <i className="ri-flag-2-line mr-1"></i>
                Report Product
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && product && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Product</h3>
              <button onClick={() => { setShowShareModal(false); setCopiedShare(false); }} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-md overflow-hidden bg-gray-100">
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">No image</div>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-600">₦{effectivePrice.toLocaleString()}</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                {getShareMessageByCategory(product.category, product.name, product.price)}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Share Link</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={buildShareUrl()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    onClick={async () => {
                      const link = buildShareUrl();
                      try {
                        await navigator.clipboard.writeText(link);
                        setCopiedShare(true);
                        setTimeout(() => setCopiedShare(false), 2000);
                      } catch (e) {
                        alert('Could not copy link');
                      }
                    }}
                    className="px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                  >
                    {copiedShare ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">This link includes Open Graph tags so WhatsApp/Twitter can render a rich preview.</p>
              </div>

              <div className="flex justify-end">
                <button onClick={() => { setShowShareModal(false); setCopiedShare(false); }} className="px-4 py-2 bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200">Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Login Required</h3>
              <p className="text-gray-600 mb-6">Please log in to continue with this action</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowLoginPrompt(false);
                    navigate('/sell');
                  }}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Log In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post-delivery Review Prompt Modal */}
      {showReviewPrompt && product && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Rate your experience</h3>
              <button onClick={() => setShowReviewPrompt(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">How was your purchase from {product?.seller?.name}? Leave a quick rating and review.</p>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rating:</span>
                {[1,2,3,4,5].map(star => (
                  <button key={star} onClick={() => setNewRating(star)}>
                    <i className={`ri-star-fill ${star <= newRating ? 'text-yellow-400' : 'text-gray-300'} text-xl`}></i>
                  </button>
                ))}
              </div>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write your review..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {reviewError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{reviewError}</p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowReviewPrompt(false)} className="px-4 py-2 bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200">Cancel</button>
                <button
                  onClick={async () => {
                    if (!product) return;
                    setReviewError('');
                    try {
                      await addReview(product.id, newRating, newComment);
                      setNewComment('');
                      const r = await getListingReviews(product.id);
                      setReviews(r);
                      const stats = await getAverageRatingForListing(product.id);
                      setRatingStats(stats);
                      setShowReviewPrompt(false);
                    } catch (error: any) {
                      setReviewError(toUserMessage(error));
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Submit Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Complete Purchase</h3>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {cashbackInfo?.enabled && cashbackInfo.amount > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <p className="text-sm text-teal-800">
                    Cashback preview for this order: ₦{cashbackInfo.amount.toLocaleString()}
                  </p>
                </div>
              )}

              {(product?.deliveryOption || product?.pickupOption) && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Fulfillment</p>
                  {product?.deliveryOption && product?.pickupOption ? (
                    <div className="mt-1 flex gap-4 items-center">
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="radio"
                          value="delivery"
                          checked={purchaseForm.fulfillmentMethod === 'delivery'}
                          onChange={() => setPurchaseForm(prev => ({ ...prev, fulfillmentMethod: 'delivery' }))}
                          className="mr-2"
                        />
                        Delivery
                      </label>
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="radio"
                          value="pickup"
                          checked={purchaseForm.fulfillmentMethod === 'pickup'}
                          onChange={() => setPurchaseForm(prev => ({ ...prev, fulfillmentMethod: 'pickup', shareLocation: false }))}
                          className="mr-2"
                        />
                        Pickup
                      </label>
                    </div>
                  ) : product?.deliveryOption ? (
                    <p className="text-xs text-gray-600">Seller offers delivery.</p>
                  ) : product?.pickupOption ? (
                    <p className="text-xs text-gray-600">Seller offers pickup.</p>
                  ) : null}
                </div>
              )}

              {purchaseForm.fulfillmentMethod === 'pickup' && product?.pickupAddress && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700">Pickup address: {product.pickupAddress}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Your Name</label>
                <input
                  type="text"
                  value={purchaseForm.name}
                  onChange={(e) => { setPurchaseForm(prev => ({ ...prev, name: e.target.value })); if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: undefined })); }}
                  maxLength={80}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md ${fieldErrors.name ? 'border-red-400' : 'border-gray-300'}`}
                />
                {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={purchaseForm.phone}
                  onChange={(e) => { setPurchaseForm(prev => ({ ...prev, phone: e.target.value })); if (fieldErrors.phone) setFieldErrors(prev => ({ ...prev, phone: undefined })); }}
                  placeholder="e.g., 08012345678 or +2348012345678"
                  maxLength={20}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md ${fieldErrors.phone ? 'border-red-400' : 'border-gray-300'}`}
                />
                {fieldErrors.phone && <p className="mt-1 text-xs text-red-600">{fieldErrors.phone}</p>}
              </div>

              {purchaseForm.fulfillmentMethod === 'delivery' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Delivery Address</label>
                    <input
                      type="text"
                      value={purchaseForm.address}
                      onChange={(e) => { setPurchaseForm(prev => ({ ...prev, address: e.target.value })); if (fieldErrors.address) setFieldErrors(prev => ({ ...prev, address: undefined })); }}
                      placeholder="e.g., Block A, Room 12, Hostel Name"
                      maxLength={250}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md ${fieldErrors.address ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {fieldErrors.address
                      ? <p className="mt-1 text-xs text-red-600">{fieldErrors.address}</p>
                      : <p className="mt-1 text-xs text-gray-500">Type an address, or use your current location below.</p>
                    }
                  </div>

                  <div className="relative flex items-center my-1">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="px-2 text-xs uppercase text-gray-400">or</span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <i className="ri-map-pin-user-line text-blue-600 text-lg mt-0.5"></i>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">Use my current location</p>
                        <p className="text-xs text-blue-800">
                          We'll pin your location for the seller and let you share live tracking during delivery. Make sure your device location is turned on.
                        </p>
                      </div>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={purchaseForm.shareLocation}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setPurchaseForm(prev => ({ ...prev, shareLocation: on, coordinates: on ? prev.coordinates : undefined }));
                          }}
                        />
                        <div className="w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 relative transition">
                          <div className="absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5" />
                        </div>
                      </label>
                    </div>

                    {purchaseForm.shareLocation && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            disabled={gettingLocation}
                            onClick={() => {
                              if (!navigator.geolocation) {
                                setLocationHint('Geolocation is not supported on this device.');
                                return;
                              }
                              setLocationHint(null);
                              setGettingLocation(true);

                              // Collect multiple GPS readings and only accept the precise one.
                              // First reading is often an IP-based fallback (can place you in the
                              // wrong city). We wait for accuracy to drop below 50 m, or keep the
                              // best reading we've seen after ~25 s.
                              let bestPos: GeolocationPosition | null = null;
                              const ACCURACY_TARGET = 50; // meters
                              const HARD_TIMEOUT_MS = 25000;

                              let watchId: number | null = null;
                              let timeoutId: number | null = null;

                              const finish = (pos: GeolocationPosition | null, errMsg?: string) => {
                                if (watchId != null) navigator.geolocation.clearWatch(watchId);
                                if (timeoutId != null) window.clearTimeout(timeoutId);
                                setGettingLocation(false);
                                if (!pos) {
                                  setLocationHint(errMsg || 'Could not get your location. Make sure device location is on and try outdoors or near a window.');
                                  return;
                                }
                                const { latitude, longitude, accuracy } = pos.coords;
                                setPurchaseForm(prev => ({ ...prev, coordinates: { lat: latitude, lng: longitude } }));
                                setLocationAccuracy(accuracy ?? null);
                                if (accuracy && accuracy > 500) {
                                  setLocationHint('Your location looks imprecise. For best results, enable GPS, go outdoors/near a window, and press Refresh.');
                                } else if (accuracy && accuracy > 100) {
                                  setLocationHint('Location captured, but accuracy is moderate. Press Refresh if it looks wrong.');
                                } else {
                                  setLocationHint(null);
                                }
                              };

                              watchId = navigator.geolocation.watchPosition(
                                (pos) => {
                                  if (!bestPos || (pos.coords.accuracy ?? Infinity) < (bestPos.coords.accuracy ?? Infinity)) {
                                    bestPos = pos;
                                  }
                                  if ((pos.coords.accuracy ?? Infinity) <= ACCURACY_TARGET) {
                                    finish(pos);
                                  }
                                },
                                (err) => {
                                  console.error('Location error', err);
                                  if (bestPos) { finish(bestPos); return; }
                                  finish(null, err.code === err.PERMISSION_DENIED
                                    ? 'Location permission denied. Please enable location access in your browser settings and try again.'
                                    : 'Could not get your location. Make sure device location is on and try outdoors.');
                                },
                                { enableHighAccuracy: true, maximumAge: 0, timeout: HARD_TIMEOUT_MS }
                              );

                              timeoutId = window.setTimeout(() => {
                                finish(bestPos, bestPos ? undefined : 'Timed out getting a precise location. Move outdoors/near a window and try again.');
                              }, HARD_TIMEOUT_MS);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-60"
                          >
                            <i className={`ri-navigation-line ${gettingLocation ? 'animate-pulse' : ''}`}></i>
                            {gettingLocation ? 'Getting precise fix…' : purchaseForm.coordinates ? 'Refresh location' : 'Turn on & capture my location'}
                          </button>
                          {purchaseForm.coordinates && (
                            <span className="text-xs text-gray-700">
                              Pinned: {purchaseForm.coordinates.lat?.toFixed(5)}, {purchaseForm.coordinates.lng?.toFixed(5)}
                              {locationAccuracy != null && <> · ±{Math.round(locationAccuracy)}m</>}
                            </span>
                          )}
                        </div>
                        {purchaseForm.coordinates && (
                          <a
                            href={`https://www.google.com/maps?q=${purchaseForm.coordinates.lat},${purchaseForm.coordinates.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                          >
                            <i className="ri-external-link-line"></i>
                            Preview pin on Google Maps
                          </a>
                        )}
                        {locationHint && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            {locationHint}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {product?.deliveryOption && (
                    <p className="text-xs text-gray-500">Delivery may incur a fee set by the seller.</p>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Buyer Note (optional)</label>
                <textarea
                  placeholder="e.g., leave with roommate"
                  value={purchaseForm.buyerNote}
                  onChange={(e) => { setPurchaseForm(prev => ({ ...prev, buyerNote: e.target.value })); if (fieldErrors.note) setFieldErrors(prev => ({ ...prev, note: undefined })); }}
                  rows={3}
                  maxLength={500}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md ${fieldErrors.note ? 'border-red-400' : 'border-gray-300'}`}
                />
                <div className="mt-1 flex items-center justify-between text-xs">
                  {fieldErrors.note
                    ? <span className="text-red-600">{fieldErrors.note}</span>
                    : <span className="text-gray-400">Max 500 characters.</span>
                  }
                  <span className="text-gray-400">{purchaseForm.buyerNote.length}/500</span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <i className="ri-shield-check-line mr-2"></i>
                  Payment will be held in escrow until delivery is confirmed
                </p>
              </div>

              {purchaseError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">
                  {purchaseError}
                </div>
              )}

              <div className="text-xs text-gray-600">
                Wallet balance: <span className="font-medium">₦{(wallet?.balance ?? 0).toLocaleString()}</span>
              </div>

              <button
                onClick={handleWalletPurchase}
                disabled={isPurchasing || (wallet?.balance ?? 0) < effectivePrice}
                title={(wallet?.balance ?? 0) < effectivePrice ? 'Insufficient wallet balance' : ''}
                className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-60"
              >
                {isPurchasing ? 'Processing…' : `Pay from Wallet - ₦${effectivePrice.toLocaleString()}`}
              </button>

              <button
                onClick={initializePaystackPurchase}
                disabled={isPurchasing}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {isPurchasing ? 'Processing…' : `Pay with Paystack - ₦${effectivePrice.toLocaleString()}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient Funds Modal */}
      {showInsufficientFunds && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Insufficient Funds</h3>
              <button
                onClick={() => setShowInsufficientFunds(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-wallet-line text-2xl text-red-600"></i>
              </div>
              
              <div>
                <p className="text-gray-600 mb-2">You need ₦{effectivePrice.toLocaleString()} to purchase this item.</p>
                <p className="text-sm text-gray-500">Current wallet balance: ₦{(wallet?.balance ?? 0).toLocaleString()}</p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowInsufficientFunds(false);
                    navigate('/wallet');
                  }}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Fund Wallet
                </button>
                <button
                  onClick={() => setShowInsufficientFunds(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;



